import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CircleCheck, RefreshCw, SearchX } from 'lucide-react';
import {
  AppButton,
  Callout,
  EmptyState,
  FileViewerModal,
  IconButton,
  StatusBadge,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import CommonToaster from '../Common/CommonToaster';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import TicketThread from './TicketThread';
import TicketComposer from './TicketComposer';
import TicketSidePanel from './TicketSidePanel';
import TicketPriority from './TicketPriority';
import { categoryLabel, firstName, formatDate, isActive, isStaffRole } from './supportVocabulary';
import { getTicketCreatorName } from './supportIdentity';

/**
 * What the requester is told about where their request stands — one sentence,
 * and what they can do next. Staff read the same state off the side panel.
 */
function requesterNotice(ticket) {
  if (ticket.status === 'resolved') {
    return { tone: 'success', text: 'This request is resolved. If the problem comes back, reply below and it reopens.' };
  }
  if (ticket.status === 'closed') {
    return { tone: 'info', text: 'This request is closed. Replying below reopens it.' };
  }
  if (ticket.awaiting === 'requester') {
    return { tone: 'info', text: 'Support has replied. Answer below if you need anything else — or mark it solved.' };
  }
  return { tone: 'info', text: 'We’ve got your request. Replies will appear here — nothing else to do for now.' };
}

function DetailSkeleton() {
  return (
    <div className="tw-flex tw-flex-col tw-gap-5" aria-busy="true" aria-label="Loading the request">
      <Shimmer height={20} />
      <Shimmer height={64} />
      <div className="tw-grid tw-gap-5 min-[992px]:tw-grid-cols-[minmax(0,1fr)_20rem]">
        <Shimmer height={360} />
        <Shimmer height={360} />
      </div>
    </div>
  );
}

/**
 * One ticket, for whoever may open it — the requester (`/s/support/:id`,
 * `/i/support/:id`), institute staff (`/support/tickets/:id`) or the SaaS
 * admin (`/sa/support/:id`). The server decides what each may read; this page
 * decides only which tools to show.
 *
 * Props:
 *   backPath  — the list this ticket came from
 *   backLabel — what that list is called
 */
const TicketDetailPage = ({ backPath, backLabel = 'All requests' }) => {
  const { id } = useParams();
  const user = getCurrentUser();
  const isStaff = isStaffRole(user?.usertype);
  const isPlatform = user?.usertype === 'tenantadmin';

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [viewing, setViewing] = useState(false);
  const [solving, setSolving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const notify = (message, type = 'success') => setToast({ show: true, message, type });
  const failMessage = (err, fallback) => err?.response?.data?.message || fallback;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/support/tickets/${id}`);
      setTicket(data.ticket);
      setLoadError(null);
    } catch (err) {
      setLoadError(err?.response?.status === 404 ? 'missing' : 'failed');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const send = async ({ text, internal, resolve }) => {
    try {
      const { data } = await apiClient.post(`/support/tickets/${id}/comment`, { text, isInternal: internal });
      let next = data.ticket;
      if (resolve && isStaff) {
        const { data: updated } = await apiClient.put(`/support/tickets/${id}`, { status: 'resolved' });
        next = updated.ticket;
      }
      setTicket(next);
      notify(data.reopened ? 'Reply sent — the request is open again.' : internal ? 'Note added.' : resolve ? 'Reply sent and ticket resolved.' : 'Reply sent.');
      return true;
    } catch (err) {
      notify(failMessage(err, 'Your reply wasn’t sent. Try again.'), 'error');
      return false;
    }
  };

  const saveProperties = async (changes) => {
    try {
      const { data } = await apiClient.put(`/support/tickets/${id}`, changes);
      setTicket(data.ticket);
      notify('Ticket updated.');
    } catch (err) {
      notify(failMessage(err, 'The changes weren’t saved. Try again.'), 'error');
    }
  };

  const markSolved = async () => {
    setSolving(true);
    try {
      const { data } = await apiClient.post(`/support/tickets/${id}/resolve`);
      setTicket(data.ticket);
      notify('Marked as solved. Thanks for letting us know.');
    } catch (err) {
      notify(failMessage(err, 'That didn’t go through. Try again.'), 'error');
    } finally {
      setSolving(false);
    }
  };

  const back = (
    <Link
      to={backPath}
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-self-start tw-rounded-sm tw-text-[0.8125rem] tw-font-medium tw-text-content-muted tw-no-underline tw-focus-ring hover:tw-text-primary"
    >
      <ArrowLeft size={16} aria-hidden="true" /> {backLabel}
    </Link>
  );

  if (loading && !ticket) return <DetailSkeleton />;

  if (!ticket) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {back}
        {loadError === 'missing' ? (
          <EmptyState
            icon={SearchX}
            title="This request isn’t available"
            description="It may have been removed, or it belongs to someone else."
            action={<Link to={backPath} className="tw-text-[0.8125rem] tw-font-semibold tw-text-primary">Back to {backLabel.toLowerCase()}</Link>}
          />
        ) : (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            The request couldn’t be loaded. Check your connection and try again.
          </Callout>
        )}
      </div>
    );
  }

  const isRequester = String(ticket.createdBy || '') === String(user?.userId || '');
  const requesterName = getTicketCreatorName(ticket);
  const notice = !isStaff ? requesterNotice(ticket) : null;
  const canSolve = isRequester && isActive(ticket.status);

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {back}

      <header className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-m-0 tw-font-mono tw-text-xs tw-font-semibold tw-text-content-subtle">{ticket.ticketId}</p>
          <h1 className="tw-m-0 tw-mt-1 tw-break-words tw-text-[1.375rem] tw-font-bold tw-leading-tight tw-tracking-tight tw-text-content">
            {ticket.title}
          </h1>
          <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-text-[0.8125rem] tw-text-content-muted">
            <StatusBadge status={ticket.status} domain="supportTicket" />
            <TicketPriority priority={ticket.priority} />
            <span aria-hidden="true">·</span>
            <span>{categoryLabel(ticket.category)}</span>
            <span aria-hidden="true">·</span>
            <span>
              Opened {formatDate(ticket.createdAt)}
              {!isRequester && ` by ${requesterName}`}
            </span>
            {isPlatform && ticket.tenantName && (
              <>
                <span aria-hidden="true">·</span>
                <span>{ticket.tenantName}</span>
              </>
            )}
          </div>
        </div>
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
          <IconButton label="Refresh" icon={RefreshCw} size="sm" variant="outline" onClick={load} disabled={loading} />
          {canSolve && (
            <AppButton size="sm" variant="outline" onClick={markSolved} disabled={solving}>
              <CircleCheck size={16} aria-hidden="true" /> {solving ? 'Saving…' : 'Mark as solved'}
            </AppButton>
          )}
        </div>
      </header>

      {notice && <Callout tone={notice.tone}>{notice.text}</Callout>}

      <div className="tw-grid tw-items-start tw-gap-5 min-[992px]:tw-grid-cols-[minmax(0,1fr)_20rem]">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-5">
          <TicketThread
            ticket={ticket}
            viewerId={user?.userId}
            viewerIsStaff={isStaff}
            onOpenAttachment={() => setViewing(true)}
          />
          <TicketComposer
            canWriteInternal={isStaff}
            isDone={!isActive(ticket.status)}
            isRequester={isRequester}
            requesterName={firstName(requesterName)}
            onSend={send}
          />
        </div>

        <aside aria-label="Ticket details" className="min-[992px]:tw-sticky min-[992px]:tw-top-4">
          <TicketSidePanel
            ticket={ticket}
            isStaff={isStaff}
            showInstitute={isPlatform}
            myName={user?.name || ''}
            onSave={saveProperties}
            onCopied={() => notify('Ticket number copied.')}
          />
        </aside>
      </div>

      {ticket.attachmentUrl && (
        <FileViewerModal
          show={viewing}
          onHide={() => setViewing(false)}
          fileUrl={ticket.attachmentUrl}
          fileName={`${ticket.ticketId} screenshot`}
          fileType="image/png"
        />
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default TicketDetailPage;
